export type OperationType = 'text_to_video' | 'image_to_video' | 'first_last_frame' | 'multiple_images';

export interface ModelConfig {
  id: string;
  name: string;
  brand: string;
  brandColor: string;
  brandIcon: string;
  tag: string;
  description: string;
  color: string;
  // Per-second credit costs based on resolution (with audio pricing)
  // Formula: 1 credit = $0.10 of fal.ai cost
  creditsPerSecond: {
    '480p': number;
    '720p': number;
    '1080p': number;
    '4k': number;
  };
  operations: OperationType[];
  durations: string[];
  aspectRatios: string[];
  aspectRatiosItv: string[];
  resolutions: string[];
  defaultDuration: string;
  defaultAspectRatio: string;
  defaultResolution: string;
  maxImages: number;
  falModels: Partial<Record<OperationType, string>>;
  supportsNegativePrompt: boolean;
  supportsSeed: boolean;
  supportsAudio: boolean;
  supportsAutoFix: boolean;
  /** Kept for lookups (existing orders, pricing) but not offered in the manual picker. */
  hidden?: boolean;
}

// Credit pricing per second based on fal.ai costs
// Formula: 1 credit ≈ $0.10 of fal.ai cost (with audio pricing)
// This gives ~50% margin on credit purchases ($0.20/credit)
//
// fal.ai pricing (with audio):
// - H3 Max (veo31): $0.08/s (768P), $0.16/s (1080P), no 4K
// - Gemini Omni Flash (veo31_fast): $0.10/s (720p), $0.15/s (1080p), $0.30/s (4K)
// - Sora 2: $0.30/s (720p)
// - Sora 2 Pro: $0.50/s (1080p)
// - Grok Video: $0.05/s (480p), $0.07/s (720p)
// - Kling O3: $0.224/s (720p with audio)
// - Kling V3: $0.336/s (720p with audio)

export const MODEL_CONFIGS: ModelConfig[] = [
  {
    id: 'veo31_fast',
    // Id/endpoints stay Veo: the backend runs these on Gemini Omni Flash (Veo retired 2026-10-22).
    name: 'Gemini Omni Flash',
    brand: 'Google',
    brandColor: '#4285F4',
    brandIcon: 'https://www.google.com/favicon.ico',
    tag: 'Fast & Cheap',
    description: "Google's fast video model. 720p, 1080p or 4K, native audio, first and last frame and reference images. You can extend the video by about 7 seconds after creation.",
    color: '#06B6D4',
    // $0.10-0.15/s → 2 credits/s (720p/1080p), $0.30/s → 4 credits/s (4K)
    creditsPerSecond: { '480p': 2, '720p': 2, '1080p': 2, '4k': 4 },
    operations: ['text_to_video', 'image_to_video', 'first_last_frame', 'multiple_images'],
    durations: ['4s', '6s', '8s'],
    aspectRatios: ['16:9', '9:16'],
    aspectRatiosItv: ['auto', '16:9', '9:16'],
    resolutions: ['720p', '1080p', '4k'],
    defaultDuration: '4s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    maxImages: 3,
    falModels: {
      text_to_video: 'fal-ai/veo3.1/fast',
      image_to_video: 'fal-ai/veo3.1/fast/image-to-video',
      first_last_frame: 'fal-ai/veo3.1/fast/first-last-frame-to-video',
      multiple_images: 'fal-ai/veo3.1/reference-to-video',
    },
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
  },
  {
    id: 'veo31',
    // Id/endpoints stay Veo: the backend runs these on MiniMax H3 Max (Veo retired 2026-10-22).
    name: 'H3 Max',
    brand: 'MiniMax',
    brandColor: '#E73562',
    brandIcon: 'https://www.minimax.io/favicon.ico',
    tag: 'Best Quality',
    description: "MiniMax's high-quality model. Up to 1080p, native audio, seed control. You can extend the video by about 7 seconds after creation.",
    color: '#3B82F6',
    // 4 credits/s; no 4K (H3 Max tops out at 1080p, backend bills a stray 4K ask as 1080p).
    creditsPerSecond: { '480p': 4, '720p': 4, '1080p': 4, '4k': 4 },
    // No Multiple Images: the backend runs reference on Gemini Omni Flash (veo31_fast).
    operations: ['text_to_video', 'image_to_video', 'first_last_frame'],
    durations: ['4s', '6s', '8s'],
    aspectRatios: ['16:9', '9:16'],
    aspectRatiosItv: ['auto', '16:9', '9:16'],
    resolutions: ['720p', '1080p'],
    defaultDuration: '8s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    maxImages: 3,
    falModels: {
      text_to_video: 'fal-ai/veo3.1',
      image_to_video: 'fal-ai/veo3.1/image-to-video',
      first_last_frame: 'fal-ai/veo3.1/first-last-frame-to-video',
      multiple_images: 'fal-ai/veo3.1/reference-to-video',
    },
    supportsNegativePrompt: false,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
  },
  {
    id: 'sora2',
    name: 'Sora 2',
    brand: 'OpenAI',
    brandColor: '#10A37F',
    brandIcon: 'https://openai.com/favicon.ico',
    tag: 'Creative',
    description: 'OpenAI\'s creative model. Up to 12s with native audio. You can edit the video with a prompt after creation.',
    color: '#10B981',
    // $0.30/s → 3 credits/s (720p only)
    creditsPerSecond: { '480p': 3, '720p': 3, '1080p': 3, '4k': 3 },
    operations: ['text_to_video', 'image_to_video'],
    durations: ['4s', '8s', '12s'],
    aspectRatios: ['16:9', '9:16'],
    aspectRatiosItv: ['auto', '16:9', '9:16'],
    resolutions: ['720p'],
    defaultDuration: '8s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    maxImages: 1,
    falModels: {
      text_to_video: 'fal-ai/sora-2/text-to-video',
      image_to_video: 'fal-ai/sora-2/image-to-video',
    },
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
    hidden: true,
  },
  {
    id: 'sora2_pro',
    name: 'Sora 2 Pro',
    brand: 'OpenAI',
    brandColor: '#10A37F',
    brandIcon: 'https://openai.com/favicon.ico',
    tag: '1080p',
    description: 'Higher quality Sora 2 with 1080p support. You can edit the video with a prompt after creation.',
    color: '#059669',
    // $0.50/s → 5 credits/s (1080p)
    creditsPerSecond: { '480p': 5, '720p': 5, '1080p': 5, '4k': 5 },
    operations: ['text_to_video', 'image_to_video'],
    durations: ['4s', '8s', '12s'],
    aspectRatios: ['16:9', '9:16'],
    aspectRatiosItv: ['auto', '16:9', '9:16'],
    resolutions: ['720p', '1080p'],
    defaultDuration: '8s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    maxImages: 1,
    falModels: {
      text_to_video: 'fal-ai/sora-2/text-to-video/pro',
      image_to_video: 'fal-ai/sora-2/image-to-video/pro',
    },
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
    hidden: true,
  },
  {
    id: 'grok',
    name: 'Grok Video',
    brand: 'xAI',
    brandColor: '#1D1D1F',
    brandIcon: 'https://x.ai/favicon.ico',
    tag: 'Audio + 15s',
    description: 'xAI\'s video model. Up to 15s with native audio. You can edit the video with a prompt after creation.',
    color: '#6366F1',
    // $0.05/s (480p) → 1 credit/s, $0.07/s (720p) → 1 credit/s (rounded up)
    creditsPerSecond: { '480p': 1, '720p': 1, '1080p': 1, '4k': 1 },
    operations: ['text_to_video', 'image_to_video'],
    durations: ['5s', '6s', '10s', '15s'],
    aspectRatios: ['16:9', '4:3', '3:2', '1:1', '2:3', '3:4', '9:16'],
    aspectRatiosItv: ['16:9', '4:3', '3:2', '1:1', '2:3', '3:4', '9:16'],
    resolutions: ['480p', '720p'],
    defaultDuration: '6s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    maxImages: 1,
    falModels: {
      text_to_video: 'xai/grok-imagine-video/text-to-video',
      image_to_video: 'xai/grok-imagine-video/image-to-video',
    },
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: true,
    supportsAutoFix: false,
  },
  {
    id: 'wan26',
    name: 'WAN 2.6',
    brand: 'Wan AI',
    brandColor: '#8B5CF6',
    brandIcon: 'https://wan.video/favicon.ico',
    tag: 'Multi-shot + 1080p',
    description: 'Alibaba\'s WAN 2.6 model. Multi-shot support, 1080p, up to 15s. Great for cinematic storytelling.',
    color: '#8B5CF6',
    // $0.10/s (720p) → 1 credit/s, $0.15/s (1080p) → 2 credits/s
    creditsPerSecond: { '480p': 1, '720p': 1, '1080p': 2, '4k': 2 },
    operations: ['text_to_video', 'image_to_video'],
    durations: ['5s', '10s', '15s'],
    aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
    aspectRatiosItv: ['16:9', '9:16', '1:1', '4:3', '3:4'],
    resolutions: ['720p', '1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    maxImages: 1,
    falModels: {
      text_to_video: 'wan/v2.6/text-to-video',
      image_to_video: 'wan/v2.6/image-to-video',
    },
    supportsNegativePrompt: true,
    supportsSeed: true,
    supportsAudio: true,
    supportsAutoFix: false,
  },
  {
    id: 'kling_o3',
    name: 'Kling O3',
    brand: 'Kling',
    brandColor: '#F97316',
    brandIcon: 'https://klingai.com/favicon.ico',
    tag: 'Up to 15s',
    description: 'Newest Kling (3.0). Up to 15s, 1:1 support, native audio generation.',
    color: '#F97316',
    // $0.224/s → 2 credits/s (720p with audio)
    creditsPerSecond: { '480p': 2, '720p': 2, '1080p': 2, '4k': 2 },
    operations: ['text_to_video', 'image_to_video'],
    durations: ['3s', '5s', '10s', '15s'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    aspectRatiosItv: ['16:9', '9:16', '1:1'],
    resolutions: ['720p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    maxImages: 1,
    falModels: {
      text_to_video: 'fal-ai/kling-video/o3/pro/text-to-video',
      image_to_video: 'fal-ai/kling-video/o3/pro/image-to-video',
    },
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: true,
    supportsAutoFix: false,
  },
  {
    id: 'kling_v3',
    name: 'Kling V3',
    brand: 'Kling',
    brandColor: '#D97706',
    brandIcon: 'https://klingai.com/favicon.ico',
    tag: 'Up to 15s',
    description: 'Kling V3 (3.0 Pro) with negative prompt, CFG control, and multi-shot support.',
    color: '#D97706',
    // $0.336/s → 3 credits/s (720p with audio)
    creditsPerSecond: { '480p': 3, '720p': 3, '1080p': 3, '4k': 3 },
    operations: ['text_to_video', 'image_to_video'],
    durations: ['3s', '5s', '10s', '15s'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    aspectRatiosItv: ['16:9', '9:16', '1:1'],
    resolutions: ['720p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    maxImages: 1,
    falModels: {
      text_to_video: 'fal-ai/kling-video/v3/pro/text-to-video',
      image_to_video: 'fal-ai/kling-video/v3/pro/image-to-video',
    },
    supportsNegativePrompt: true,
    supportsSeed: false,
    supportsAudio: true,
    supportsAutoFix: false,
  },
];

// i18n keys for operation labels - use with t('operations.textToVideo') etc.
export const OPERATION_LABEL_KEYS: Record<OperationType, string> = {
  text_to_video: 'operations.textToVideo',
  image_to_video: 'operations.imageToVideo',
  first_last_frame: 'operations.firstLastFrame',
  multiple_images: 'operations.multipleImages',
};

// i18n keys for operation descriptions - use with t('operations.descTextToVideo') etc.
export const OPERATION_DESC_KEYS: Record<OperationType, string> = {
  text_to_video: 'operations.descTextToVideo',
  image_to_video: 'operations.descImageToVideo',
  first_last_frame: 'operations.descFirstLastFrame',
  multiple_images: 'operations.descMultipleImages',
};

// i18n keys for setting tooltips - use with t('generator.tooltipAspectRatio') etc.
export const SETTING_TOOLTIP_KEYS = {
  aspectRatio: 'generator.tooltipAspectRatio',
  duration: 'generator.tooltipDuration',
  quality: 'generator.tooltipQuality',
  model: 'models.tooltipModel',
  negativePrompt: 'generator.tooltipNegativePrompt',
  seed: 'generator.tooltipSeed',
  audio: 'generator.tooltipAudio',
  autoFix: 'generator.tooltipAutoFix',
};

// i18n keys for model descriptions - use with t('models.descVeo31') etc.
export const MODEL_DESC_KEYS: Record<string, string> = {
  veo31: 'models.descVeo31',
  veo31_fast: 'models.descVeo31Fast',
  sora2: 'models.descSora2',
  sora2_pro: 'models.descSora2Pro',
  grok: 'models.descGrok',
  kling_o3: 'models.descKlingO3',
  wan26: 'models.descWan26',
  kling_v3: 'models.descKlingV3',
};

// Legacy exports for backward compatibility (hardcoded strings)
export const OPERATION_LABELS: Record<OperationType, string> = {
  text_to_video: 'Text to Video',
  image_to_video: 'Image to Video',
  first_last_frame: 'First/Last Frame',
  multiple_images: 'Multiple Images',
};

export const SETTING_TOOLTIPS = {
  aspectRatio: 'The shape of your video. 16:9 for landscape, 9:16 for portrait (social media), 1:1 for square.',
  duration: 'How long your video will be. Longer videos use more credits.',
  quality: 'Higher resolution produces sharper videos but takes longer to generate.',
  model: 'Each model has different strengths. Try different ones to find what works best for your content.',
  negativePrompt: 'Describe what you don\'t want in the video. Helps avoid unwanted elements.',
  seed: 'Set a specific number for reproducible results. Same seed + same prompt = same video.',
  audio: 'Generate native audio/sound effects along with the video.',
  autoFix: 'Automatically rewrites prompts that might fail content policy checks.',
};

export function getModelById(id: string): ModelConfig | undefined {
  return MODEL_CONFIGS.find((m) => m.id === id);
}

export function getOperationsForModel(modelId: string): OperationType[] {
  return getModelById(modelId)?.operations ?? ['text_to_video'];
}

export function getAspectRatiosForModel(model: ModelConfig, operation: OperationType): string[] {
  if (operation === 'image_to_video') return model.aspectRatiosItv;
  return model.aspectRatios;
}

export function needsImages(operation: OperationType): boolean {
  return operation !== 'text_to_video';
}

export function getImageCount(operation: OperationType, model: ModelConfig): { min: number; max: number; labels?: string[] } {
  switch (operation) {
    case 'text_to_video':
      return { min: 0, max: 0 };
    case 'image_to_video':
      return { min: 1, max: 1 };
    case 'first_last_frame':
      return { min: 2, max: 2, labels: ['First Frame', 'Last Frame'] };
    case 'multiple_images':
      return { min: 2, max: model.maxImages };
    default:
      return { min: 0, max: 0 };
  }
}

// Operations a model no longer offers, mapped to the model that now serves them.
const OPERATION_MOVED_TO: Record<string, Partial<Record<OperationType, string>>> = {
  veo31: { multiple_images: 'veo31_fast' },
};

/** Model id to use for a (possibly stale, saved) model + operation pair. */
export function modelIdForOperation(modelId: string, operation: OperationType): string {
  return OPERATION_MOVED_TO[modelId]?.[operation] ?? modelId;
}

export function getModelsForOperation(operation: OperationType): ModelConfig[] {
  return MODEL_CONFIGS.filter((m) => !m.hidden && m.operations.includes(operation));
}

/**
 * Calculate the credit cost for a video generation based on model, duration, and resolution.
 * @param model - The model configuration
 * @param durationStr - Duration string like "4s", "8s", "12s"
 * @param resolution - Resolution string like "720p", "1080p", "4k"
 * @returns Total credits required
 */
export function calculateCredits(model: ModelConfig, durationStr: string, resolution: string): number {
  // Parse duration (e.g., "8s" -> 8)
  const durationSec = parseInt(durationStr.replace('s', ''), 10) || 8;
  
  // Normalize resolution key
  const resKey = resolution.toLowerCase() as keyof typeof model.creditsPerSecond;
  const creditsPerSec = model.creditsPerSecond[resKey] || model.creditsPerSecond['720p'];
  
  return durationSec * creditsPerSec;
}

/**
 * Get the per-second credit rate for display purposes.
 * @param model - The model configuration
 * @param resolution - Resolution string like "720p", "1080p", "4k"
 * @returns Credits per second
 */
export function getCreditsPerSecond(model: ModelConfig, resolution: string): number {
  const resKey = resolution.toLowerCase() as keyof typeof model.creditsPerSecond;
  return model.creditsPerSecond[resKey] || model.creditsPerSecond['720p'];
}

// Mirrors EXTEND_CREDITS_PER_SECOND in the backend's _veo_replacement.py.
const EXTEND_CREDITS_PER_SECOND = 3;

/**
 * Calculate credit cost for extending a video.
 * Extend generates ~7s on H3 Max extend; create-order charges 7s * 3 credits/s.
 * @returns Credits required for extend operation
 */
export function calculateExtendCredits(): number {
  return 7 * EXTEND_CREDITS_PER_SECOND;
}

/** fal_model of an order that can be extended (Veo ids, still stored, and their replacements). */
export function isExtendableFalModel(falModel: string | undefined): boolean {
  const m = (falModel || '').toLowerCase();
  return m.includes('veo3.1') || m.includes('gemini-omni-flash') || m.includes('h3-max');
}

/**
 * Calculate credit cost for editing/remixing a video (Sora 2 or Grok).
 * Since remix regenerates the video, we estimate based on the model's default duration.
 * @param falModel - The fal.ai model string from the order (e.g., "fal-ai/sora-2/text-to-video")
 * @returns Credits required for edit/remix operation
 */
export function calculateEditCredits(falModel: string | undefined): number {
  if (!falModel) return 24; // Fallback: 8s * 3 credits/s (Sora 2 default)
  
  // Determine which model was used
  if (falModel.includes('sora-2') && falModel.includes('pro')) {
    // Sora 2 Pro: 8s at 1080p = 8 * 5 = 40 credits
    const sora2Pro = MODEL_CONFIGS.find(m => m.id === 'sora2_pro');
    if (sora2Pro) return 8 * sora2Pro.creditsPerSecond['1080p'];
    return 40;
  } else if (falModel.includes('sora-2')) {
    // Sora 2: 8s at 720p = 8 * 3 = 24 credits
    const sora2 = MODEL_CONFIGS.find(m => m.id === 'sora2');
    if (sora2) return 8 * sora2.creditsPerSecond['720p'];
    return 24;
  } else if (falModel.includes('grok')) {
    // Grok: 5s at 720p = 5 * 1 = 5 credits (uses default duration)
    const grok = MODEL_CONFIGS.find(m => m.id === 'grok');
    if (grok) return 5 * grok.creditsPerSecond['720p'];
    return 5;
  }
  
  // Default fallback
  return 24;
}
